'use client';

import { trackedEntity } from '@cineamo/pulse';
import { usePulseTracker } from '@cineamo/pulse/react';
import { useTranslations } from 'next-intl';
import type React from 'react';

import MovieReleaseBadge from '@/app/[locale]/(default-layout)/program/_components/MovieReleaseBadge';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { getAgeRatingIcon } from '@/helpers/movie-helper';
import { copyToClipboard } from '@cineamo/ui/src/helpers/clipboard-helper';
import { useAgeRatingText } from '@cineamo/ui/src/helpers/movie-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';

import SharedContentTitle from '@cineamo/composables/src/content/ContentTitle';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

/**
 * Cinema-website adapter for the shared `ContentTitle`: maps `CachedContentData` onto the shared
 * layout and supplies the app-specific pieces (typed `Link`, domain-configuration theming, the
 * FSK/GRETA badges with their info modals and tracking, the share action).
 */
const ContentTitle: React.FC<{
    content?: CachedContentData;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    /** Full release date instead of the bare year — detail page only, see the shared `ContentTitle`. */
    showReleaseDate?: boolean;
    href?: HrefOrHrefWithParams<'/program/[contentId]'>;
    /** Renders the title as a semantic `<h1>` instead of a `div`. Only takes effect when no `href` is given
     * (link rows must stay non-heading elements) — pass this only from the single canonical page usage of
     * a title, never from list rows. */
    as?: 'h1';
}> = ({ showGenres, showMovieDuration, showReleaseDate, href, as, ...props }) => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const { track: pulseTrack } = usePulseTracker();
    const { track } = useAnalytics();

    const content = props.content;
    const countryCode = domainConfiguration.cinema.countryCode;

    const ageRatingText = useAgeRatingText(content?.ageRating, countryCode);
    const ageRatingElement = getAgeRatingIcon(content?.ageRating, countryCode, {
        movieTitle: content?.title,
        movieId: content?.movieId
    });

    const getPageLink = () => `${globalThis.location.origin}/program/${content?.id}`;

    const copyPageLink = function () {
        copyToClipboard(getPageLink(), t('showing-info.copied'));
    };

    const sharePageLink = function () {
        const shareData = {
            url: getPageLink()
        };
        if (!navigator.canShare?.(shareData)) {
            copyPageLink();
            return;
        }
        navigator
            .share(shareData)
            .then(() => {
                track('Share Page Link Clicked', {
                    source: 'Content Title',
                    contentId: content?.id ?? ''
                });
                pulseTrack({
                    ...trackedEntity({ movieId: content?.movieId, contentId: content?.id ?? 0 }),
                    event_type: 'share',
                    context: { content_id: content?.id }
                });
            })
            .catch(() => {});
    };

    return (
        <SharedContentTitle
            title={content?.title ?? ''}
            renderTitle={({ className, children }) => {
                if (href) {
                    return (
                        <Link
                            href={href}
                            aria-label={content?.title}
                            className={className}>
                            {children}
                        </Link>
                    );
                }
                const TitleElement = as === 'h1' ? 'h1' : 'div';
                return (
                    <TitleElement
                        aria-label={content?.title}
                        className={className}>
                        {children}
                    </TitleElement>
                );
            }}
            // CachedContentData already carries the movie's release year, running time, genres and
            // GRETA payload, resolved in useCachedContentData
            movie={content}
            showMovieDuration={showMovieDuration}
            showGenres={showGenres}
            showReleaseDate={showReleaseDate}
            ageRatingBadge={ageRatingElement}
            ageRatingText={ageRatingText}
            gretaIconVariant={domainConfiguration.websiteConfiguration?.gretaIconVariant}
            titleBadges={
                /*Show release badge if it is movie and movie has release date*/
                content?.hasMovie &&
                content?.releaseDate && <MovieReleaseBadge movieReleaseDate={content?.releaseDate} />
            }
            titleActions={
                <button
                    onClick={sharePageLink}
                    className="flex shrink-0 hover:opacity-80 pointer-events-auto cursor-pointer transition-opacity duration-150">
                    <IconShareBox24 className="w-20 h-20" />
                </button>
            }
            styleMeta={{
                color: domainConfiguration.style.onBackgroundColor,
                opacity: 0.5
            }}
        />
    );
};

export default ContentTitle;
